import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Card,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { User, AuthorizationLevelInfo, AuthorizationLevel } from '../../model';
import { useAppState } from '../AppProvider/AppProvider';

const useStyles = makeStyles((theme) => ({
  widget: {
    borderRadius: theme.spacing(1),
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    marginBottom: theme.spacing(2),
  },
  widgetHeader: {
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.grey[50],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  widgetTitle: {
    fontWeight: 500,
    fontSize: '0.85rem',
    display: 'flex',
    alignItems: 'center',
  },
  widgetContent: {
    padding: theme.spacing(1.5),
  },
  avatar: {
    width: theme.spacing(5),
    height: theme.spacing(5),
    margin: '0 auto',
    marginBottom: theme.spacing(1),
    fontSize: '1rem',
  },
  userHeader: {
    textAlign: 'center',
    marginBottom: theme.spacing(1.5),
  },
  detailRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(0.6),
    padding: theme.spacing(0.2),
  },
  detailIcon: {
    marginRight: theme.spacing(0.8),
    color: theme.palette.text.secondary,
    fontSize: '1rem',
  },
  editButton: {
    marginTop: theme.spacing(1),
    fontSize: '0.8rem',
    padding: theme.spacing(0.6, 1.5),
    minHeight: '32px',
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  positionChip: {
    marginTop: theme.spacing(1),
    fontSize: '0.75rem',
    height: '22px',
  },
  compactText: {
    fontSize: '0.85rem',
  },
  sectionTitle: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.8),
    marginTop: theme.spacing(1.2),
    textTransform: 'uppercase',
  },
}));

interface UserDetailPanelProps {
  user: User;
  authorizationLevels: AuthorizationLevelInfo[];
  allUsers?: User[];
  onUserUpdate: () => void;
}

const UserDetailPanel: React.FC<UserDetailPanelProps> = ({
  user,
  authorizationLevels,
  allUsers = [],
  onUserUpdate,
}) => {
  const classes = useStyles();
  const [ctx] = useAppState() ?? [];
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editedUser, setEditedUser] = useState(user);
  const [saving, setSaving] = useState(false);
  const [selectedManager, setSelectedManager] = useState<number | null>(null);

  useEffect(() => {
    console.log('[UserDetailPanel] User prop updated:', user);
    setEditedUser(user);
    setSelectedManager(user.reportTo?.id || null);
  }, [user]);

  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const handleEditClick = () => {
    setEditedUser({ ...user });
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    if (saving) return; // Prevent multiple saves
    
    setSaving(true);
    try {
      const headers = {
        'authorization': ctx?.apiKey ?? '',
        'Content-Type': 'application/json'
      };
      
      let allSuccessful = true;
      let errorMessage = '';

      // Update reporting relationship if changed
      if (selectedManager !== user.reportTo?.id) {
        const reportingResponse = await fetch(`${ctx?.baseUrl}/user-hierarchy/update-reporting`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            userId: editedUser.id,
            newManagerId: selectedManager,
          }),
        });

        if (!reportingResponse.ok) {
          const error = await reportingResponse.json();
          errorMessage += `Failed to update reporting: ${error.error || 'Unknown error'}. `;
          allSuccessful = false;
        }
      }

      // Update authorization level if changed
      const currentAuthLevel = getAuthorizationLevelInfo(user.authorizationLevel);
      const newAuthLevel = getAuthorizationLevelInfo(editedUser.authorizationLevel);
      
      if (newAuthLevel?.value !== currentAuthLevel?.value) {
        console.log('[UserDetailPanel] Authorization level changed, updating from', currentAuthLevel?.name, 'to', newAuthLevel?.name);
        const positionResponse = await fetch(`${ctx?.baseUrl}/user-hierarchy/update-position`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            userId: editedUser.id,
            authorizationLevel: newAuthLevel?.value,
          }),
        });

        if (!positionResponse.ok) {
          const error = await positionResponse.json();
          errorMessage += `Failed to update position: ${error.error || 'Unknown error'}. `;
          allSuccessful = false;
        }
      }

      if (allSuccessful) {
        setEditDialogOpen(false);
        onUserUpdate(); // This will refresh the data in parent
      } else {
        console.error('Save errors:', errorMessage);
        // Could show a user-friendly error message here
      }
    } catch (error) {
      console.error('Error updating user:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditDialogOpen(false);
    setEditedUser(user);
  };

  const getAuthorizationLevelColor = (level: number): string => {
    const colors = ['#f44336', '#ff9800', '#2196f3', '#4caf50', '#9c27b0', '#607d8b'];
    return colors[level - 1] || '#757575';
  };

  const getAuthorizationLevelInfo = (authorizationLevel: any): AuthorizationLevelInfo | null => {
    if (!authorizationLevel) return null;
    
    // Try to find by numeric value first
    const numericValue = Number(authorizationLevel);
    if (!isNaN(numericValue)) {
      const levelByValue = authorizationLevels.find(l => l.value === numericValue);
      if (levelByValue) return levelByValue;
    }
    
    // Try to find by string name (for enum values like "STAFF")
    const levelByName = authorizationLevels.find(l => 
      l.name.toUpperCase() === String(authorizationLevel).toUpperCase()
    );
    if (levelByName) return levelByName;
    
    return null;
  };

  return (
    <>
      <Card className={classes.widget}>
        <Box className={classes.widgetHeader}>
          <Typography className={classes.widgetTitle}>
            <PersonIcon style={{ marginRight: 8, fontSize: '1rem' }} />
            User Details
          </Typography>
        </Box>
        <Box className={classes.widgetContent}>
          <Box className={classes.userHeader}>
            <Avatar className={classes.avatar}>
              {getInitials(user.name)}
            </Avatar>
            <Typography variant="h6" component="h3" style={{ fontSize: '1.1rem', marginBottom: 4 }}>
              {user.name}
            </Typography>
            {user.authorizationLevel && (
              <Chip
                size="small"
                label={getAuthorizationLevelInfo(user.authorizationLevel)?.name || 'Unknown'}
                className={classes.positionChip}
                style={{
                  backgroundColor: getAuthorizationLevelColor(getAuthorizationLevelInfo(user.authorizationLevel)?.value || 6),
                  color: 'white',
                }}
              />
            )}
          </Box>

          <Typography className={classes.sectionTitle}>
            Contact Information
          </Typography>
          
          <Box className={classes.detailRow}>
            <EmailIcon className={classes.detailIcon} />
            <Typography variant="body2" className={classes.compactText}>
              {user.email || 'No email provided'}
            </Typography>
          </Box>

          <Box className={classes.detailRow}>
            <PhoneIcon className={classes.detailIcon} />
            <Typography variant="body2" className={classes.compactText}>
              {user.phone || 'No phone provided'}
            </Typography>
          </Box>

          <Box className={classes.detailRow}>
            <BusinessIcon className={classes.detailIcon} />
            <Typography variant="body2" className={classes.compactText}>
              {user.department?.name || 'No department assigned'}
            </Typography>
          </Box>

          {user.reportTo && (
            <>
              <Typography className={classes.sectionTitle}>
                Reports To
              </Typography>
              <Box className={classes.detailRow}>
                <PersonIcon className={classes.detailIcon} />
                <Typography variant="body2" className={classes.compactText}>
                  {user.reportTo.name}
                </Typography>
              </Box>
            </>
          )}

          <Button
            variant="outlined"
            color="primary"
            startIcon={<EditIcon />}
            fullWidth
            className={classes.editButton}
            onClick={handleEditClick}
          >
            Edit User
          </Button>
        </Box>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>

          <Autocomplete
            className={classes.formField}
            options={allUsers.filter(u => u.id !== user.id)}
            value={selectedManager === null ? null : allUsers.find(u => u.id === selectedManager) || null}
            onChange={(_, newValue) => {
              const newManagerId = newValue?.id || null;
              setSelectedManager(newManagerId);
              const newManager = newValue?.id ? allUsers.find(u => u.id === newValue.id) : null;
              setEditedUser({
                ...editedUser,
                reportTo: newManager || null,
              });
            }}
            getOptionLabel={(option) => option?.name || ''}
            getOptionSelected={(option, value) => option?.id === value?.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Reports To"
                placeholder="Search for a manager or leave empty for unassigned..."
                variant="outlined"
                fullWidth
              />
            )}
            renderOption={(option) => (
              <Box display="flex" alignItems="center" width="100%">
                <Avatar style={{ marginRight: 12, width: 32, height: 32, fontSize: '0.85rem' }}>
                  {getInitials(option.name)}
                </Avatar>
                <Box>
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    {option.name}
                  </Typography>
                  {option.department && (
                    <Typography variant="caption" color="textSecondary">
                      {option.department.name}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            noOptionsText="No users found"
            clearOnEscape
            clearOnBlur
            includeInputInList
            size="small"
          />

          <FormControl fullWidth className={classes.formField}>
            <InputLabel>Authorization Level</InputLabel>
            <Select
              value={getAuthorizationLevelInfo(editedUser.authorizationLevel)?.value || ''}
              onChange={(e) => {
                const selectedValue = e.target.value as number;
                const selectedLevel = authorizationLevels.find(l => l.value === selectedValue);
                setEditedUser({
                  ...editedUser,
                  authorizationLevel: selectedLevel ? selectedLevel.name.toUpperCase() as AuthorizationLevel : null
                });
              }}
            >
              <MenuItem value="">
                <em>No authorization level</em>
              </MenuItem>
              {authorizationLevels.map((level) => (
                <MenuItem key={level.value} value={level.value}>
                  <Chip
                    size="small"
                    label={level.name}
                    style={{
                      backgroundColor: getAuthorizationLevelColor(level.value),
                      color: 'white',
                      marginRight: 8,
                    }}
                  />
                  {level.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={saving}
            startIcon={<SaveIcon />}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserDetailPanel;
